import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { AuthContextType } from '../types';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { logout } = useContext(AuthContext) as AuthContextType;

  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMenuOpen &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMenuOpen, menuRef, buttonRef]);

  return (
    <nav className="bg-white/10 backdrop-blur-md border-b border-white/20 top-0 z-50 sticky bg-gradient-to-r from-purple-900 via-blue-900 to-indigo-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="flex justify-between items-center h-16">
          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={toggleMobileMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
            >
              <span className="sr-only">Open main menu</span>
              {!isMobileMenuOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center">
              <img src="/images/G_white.svg" alt="Gyaanly" className="w-8 h-8 " />
            </div>
            <span className="text-xl font-bold text-white">Gyaanly</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/creator" className="text-gray-300 hover:text-white transition-colors duration-200">
              Creator
            </Link>
            <Link to="/learn" className="text-gray-300 hover:text-white transition-colors duration-200">
              Learners
            </Link>
            <div className="relative">
              <button
                ref={buttonRef}
                onClick={toggleMenu}
                className="text-gray-300 hover:text-white transition-colors duration-200 flex items-center"
              >
                Features <span className="material-icons text-sm ml-1">expand_more</span>
              </button>
              {isMenuOpen && (
                <div
                  ref={menuRef}
                  className="absolute left-0 mt-2 bg-gradient-to-r from-purple-700 to-indigo-700 w-48 border border-white/20 rounded-xl shadow-xl py-2"
                >
                  <Link
                    to="/features/automated-workflows"
                    className="block px-4 py-2 text-gray-100 hover:text-white hover:bg-white/10 transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Automated Workflows
                  </Link>
                  <Link
                    to="/features/communication-tools"
                    className="block px-4 py-2 text-gray-100 hover:text-white hover:bg-white/10 transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Communication Tools
                  </Link>
                  <Link
                    to="/features/payment-tracking"
                    className="block px-4 py-2 text-gray-100 hover:text-white hover:bg-white/10 transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Payment Tracking
                  </Link>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2 glass-button">
            <Link to="/early-access" className="text-gray-300 hover:text-white transition-colors duration-200">
              Register
            </Link>
          </div>
          
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden absolute top-16 inset-x-0 bg-gradient-to-r from-purple-900 via-blue-900 to-indigo-900 py-4 border-t border-white/20 shadow-lg">
            <div className="flex flex-col space-y-4 px-4 sm:px-6 lg:px-8">
              <Link
                to="/creator"
                className="block text-gray-300 hover:text-white transition-colors duration-200 py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Creator
              </Link>
              <Link
                to="/learn"
                className="block text-gray-300 hover:text-white transition-colors duration-200 py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Learners
              </Link>

              <Link
                  to="#"
                  className="block text-gray-300 hover:text-white transition-colors duration-200 py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                Features
              </Link>
              <div className="flex flex-col space-y-2 ml-4">
                <Link
                  to="/features/automated-workflows"
                  className="block text-gray-300 hover:text-white transition-colors duration-200 py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                Automated Workflows
                </Link>
                <Link
                  to="/features/communication-tools"
                  className="block py-2 text-gray-100 hover:text-white hover:bg-white/10 transition-colors duration-200"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                Communication Tools
                </Link>
                <Link
                  to="/features/payment-tracking"
                  className="block py-2 text-gray-100 hover:text-white hover:bg-white/10 transition-colors duration-200"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Payment Tracking
                </Link>
              </div>
              <Link
                to="/early-access"
                className="block text-gray-300 hover:text-white transition-colors duration-200 py-2"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Register
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar; 